import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

interface HeroSectionProps {
  headline: string;
  subheadline: string;
  bgImage: string;
  label?: string;
  primaryCTA?: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  headline,
  subheadline,
  bgImage,
  label,
  primaryCTA = 'Request a Consultation',
}) => {
  const bgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (bgRef.current) {
        const y = window.scrollY * 0.38;
        bgRef.current.style.transform = `translateY(${y}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      className="relative w-full h-screen min-h-[780px] flex items-center overflow-hidden"
      aria-label="Hero"
    >
      {/* Parallax background */}
      <div
        ref={bgRef}
        className="absolute inset-0 scale-110 will-change-transform"
        style={{
          backgroundImage: `url('${bgImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 25%',
        }}
      />

      {/* Multi-layer atmospheric overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D0C0B]/95 via-[#0D0C0B]/65 to-[#0D0C0B]/30" />
      {/* Strong top band ensures nav area is always dark regardless of scroll state */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D0C0B]/70 via-transparent to-[#0D0C0B]/80" />



      {/* Vertical gold accent line — left edge */}
      <div className="absolute left-0 top-0 bottom-0 w-[3px]">
        <div className="hero-rule h-full bg-gradient-to-b from-transparent via-[#C4A84F]/60 to-transparent" />
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-28">
        <div className="max-w-[700px]">

          {/* Label row */}
          {label && (
            <div className="hero-label flex items-center gap-4 mb-10">
              <span className="w-9 h-[2px] bg-[#C4A84F] gold-line" />
              <span className="text-[#C4A84F] text-[11px] tracking-[0.42em] uppercase font-sans font-medium">
                {label}
              </span>
            </div>
          )}

          {/* H1 */}
          <h1 className="hero-h1 font-serif text-[4.2rem] md:text-[5.5rem] lg:text-[6.5rem] text-[#F5F1E8] leading-[0.95] mb-9 tracking-tight">
            {headline}
          </h1>

          {/* Gold rule */}
          <div className="hero-rule mb-8 flex items-center gap-5">
            <div className="h-[1px] w-20 bg-[#C4A84F]" />
            <div className="h-[1px] w-32 bg-[#F5F1E8]/12" />
          </div>

          {/* Sub */}
          <p className="hero-sub text-[#F5F1E8]/85 text-lg md:text-xl leading-relaxed mb-12 font-sans font-light max-w-[520px]" style={{ textShadow: '0 1px 12px rgba(0,0,0,0.5)' }}>
            {subheadline}
          </p>

          {/* CTAs */}
          <div className="hero-ctas flex flex-col sm:flex-row gap-4 mb-10">
            <a href="#estimate" className="btn-gold">
              <i className="fa-regular fa-calendar-check text-[11px]" />
              {primaryCTA}
            </a>
            <a href="tel:912-409-2031" className="btn-outline-light">
              <i className="fa-solid fa-phone text-[11px]" />
              Call 912-409-2031
            </a>
          </div>

          {/* Trust badges */}
          <div className="hero-badges flex flex-wrap gap-6">
            {[
              { icon: 'fa-check',         text: 'Free Estimates' },
              { icon: 'fa-tag',           text: '$100 Off Same-Day Signing' },
              { icon: 'fa-star',          text: '5-Star Google Rated' },
              { icon: 'fa-shield-halved', text: 'Licensed & Insured' },
            ].map((b) => (
              <span key={b.text} className="flex items-center gap-2 text-[#F5F1E8]/80 text-[12px] font-sans tracking-wide" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.6)' }}>
                <i className={`fa-solid ${b.icon} text-[#C4A84F] text-[10px]`} />
                {b.text}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Floating stat panel — bottom right */}
      <div className="hero-stat absolute bottom-16 right-8 hidden lg:grid grid-cols-2 gap-px bg-[#C4A84F]/20">
        {[
          { value: '20+',  label: 'Years' },
          { value: '500+', label: 'Projects' },
          { value: '5 ★',  label: 'Rating' },
          { value: '9',    label: 'Cities' },
        ].map((s) => (
          <div key={s.label} className="bg-[#0D0C0B]/80 backdrop-blur-md px-7 py-5 border border-[#C4A84F]/15 text-center">
            <div className="font-serif text-3xl text-[#C4A84F] leading-none">{s.value}</div>
            <div className="text-[9px] text-[#F5F1E8]/35 tracking-[0.25em] uppercase font-sans mt-1.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="hero-scroll absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 hidden md:flex">
        <span className="text-[#F5F1E8]/25 text-[9px] tracking-[0.4em] uppercase font-sans">Scroll</span>
        <div className="relative w-[1px] h-14 bg-gradient-to-b from-[#C4A84F]/60 to-transparent">
          <div className="hero-scroll-dot w-2 h-2 rounded-full bg-[#C4A84F]" />
        </div>
      </div>

      {/* Bottom edge blend */}
      <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#0D0C0B] to-transparent pointer-events-none" />

      {/* Internal link for SEO */}
      <Link to="/services" className="sr-only">View our fencing services</Link>
    </section>
  );
};

export default HeroSection;
